import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { DocumentTypeService } from './document-type.service';
import { DocumentTypeRequest } from './document-type.model';

@Component({
  selector: 'app-document-type',
  standalone: false,
  templateUrl: './document-type.component.html',
  styleUrls: ['./document-type.component.css']
})
export class DocumentTypeComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  documentTypes: any[] = [];
  filteredDocumentTypes: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  documentTypeId = 0;
  isEnabled = true;
  styleClass = '';

  nameKh = '';
  nameEn = '';

  constructor(
    private documentTypeService: DocumentTypeService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadLanguages();
    this.loadDocumentTypes('');
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);
      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }
    });
  }

  loadDocumentTypes(langCode: string) {
    this.documentTypeService.getAllDocumentTypes(langCode).subscribe((res: any) => {
      const apiData: any[] = res.data || [];
      this.documentTypes = apiData.flatMap(item =>
        item.translations.map((t: any) => ({
          documentTypeId: item.documentTypeId,
          isEnabled: item.isEnabled,
          styleClass: item.styleClass,
          languageCode: t.languageCode,
          languageId: t.languageId,
          name: t.name
        }))
      );

      this.filterDocumentTypes(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterDocumentTypes(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterDocumentTypes(tab: string) {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredDocumentTypes = this.documentTypes.filter(documentType => {
      const status = documentType.isEnabled ? 'Active' : 'Inactive';
      const matchesTab = documentType.languageCode === tab;
      const matchesSearch = !keyword ||
        `${documentType.name ?? ''} ${documentType.styleClass ?? ''} ${status} ${documentType.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(lang: string): number {
    return this.documentTypes.filter(r => r.languageCode === lang).length;
  }

  get pagedDocumentTypes(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredDocumentTypes.slice(start, end);
  }

  totalPages(): number {
    return Math.ceil(this.filteredDocumentTypes.length / this.pageSize);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.documentTypeId = id;

    this.documentTypeService.getDocumentTypeById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;
          this.documentTypeId = data.documentTypeId;
          this.isEnabled = data.isEnabled;
          this.styleClass = data.styleClass ?? '';
          this.nameKh = '';
          this.nameEn = '';

          data.translations.forEach((item: any) => {
            if (item.languageCode === 'kh') {
              this.nameKh = item.name;
            } else if (item.languageCode === 'en') {
              this.nameEn = item.name;
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: (err) => {
        this.errorMessage = 'Failed to load document type';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  resetForm() {
    this.documentTypeId = 0;
    this.isEnabled = true;
    this.styleClass = '';
    this.nameKh = '';
    this.nameEn = '';
  }

  getRequest(documentTypeId?: number): DocumentTypeRequest {
    return {
      documentTypeId: documentTypeId ?? 0,
      isEnabled: this.isEnabled,
      styleClass: this.styleClass,
      translations: [
        {
          languageId: 1,
          name: this.nameKh ?? ''
        },
        {
          languageId: 2,
          name: this.nameEn ?? ''
        }
      ]
    };
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest(0);
    this.clearMsg();
    this.documentTypeService.create(request).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  update() {
    this.clearMsg();
    const request = this.getRequest(this.documentTypeId);
    this.documentTypeService.update(this.documentTypeId, request).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.documentTypeService.delete(this.documentTypeId).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadDocumentTypes('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }
}
